import React, { Suspense } from 'react';
import { ResourceType } from '@cotera/api';
import type { ArtifactRendererProps } from './types';
import { registerArtifactRenderer } from './registry';
import { DocumentPageWithFetchedData } from '@cotera/client/app/v1/document/document.page';
import { Text } from '@cotera/client/app/components/ui';
import { ChatArtifactAccessGate } from './chat-artifact-access-gate';

type DocumentPayload = {
  documentId?: string;
  /** Set by "New document": this id does not exist server-side yet. */
  isNew?: boolean;
  folderId?: string;
};

const DocumentArtifactSkeleton: React.FC = () => (
  <div className="h-full w-full bg-background flex flex-col min-h-0 overflow-hidden">
    <div className="flex-1 min-h-0 overflow-y-auto p-5">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="h-10 w-2/3 rounded bg-coco-accent animate-pulse" />
        <div className="h-4 w-full rounded bg-coco-accent animate-pulse" />
        <div className="h-4 w-full rounded bg-coco-accent animate-pulse" />
        <div className="h-4 w-4/5 rounded bg-coco-accent animate-pulse" />
      </div>
    </div>
  </div>
);

const DocumentArtifactRenderer: React.FC<ArtifactRendererProps> = ({
  artifact,
}) => {
  const payload = artifact.payload as DocumentPayload;
  const documentId = payload?.documentId ?? artifact.artifactId;

  if (documentId === undefined || documentId === '') {
    return (
      <div className="h-full w-full flex items-center justify-center bg-background p-4">
        <Text.P className="text-muted-foreground">Document not found</Text.P>
      </div>
    );
  }

  // A brand new document has nothing to fetch and no permissions row yet, so it
  // skips the access gate the way the agent renderer does.
  if (payload?.isNew === true) {
    return (
      <DocumentPageWithFetchedData
        id={documentId}
        mode="artifact"
        isNew
        folderId={payload.folderId}
      />
    );
  }

  return (
    <ChatArtifactAccessGate
      type={ResourceType.Document}
      id={documentId}
      artifactType="document"
      fallback={<DocumentArtifactSkeleton />}
    >
      <Suspense fallback={<DocumentArtifactSkeleton />}>
        <DocumentPageWithFetchedData id={documentId} mode="artifact" />
      </Suspense>
    </ChatArtifactAccessGate>
  );
};

registerArtifactRenderer('document', DocumentArtifactRenderer);
